import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, Eye, Pencil, RotateCcw, Filter, Search, Building2 } from "lucide-react";
import { ApiService } from "../../services/api";
import checkPermission from "../../utils/permission";
import Modal from "../../components/ui/Modal";
import DepartmentSelector from "../../components/selectors/DepartmentSelector";
import DynamicList from "../../components/dynamic/List";
import DepartmentTreeSelect from "../e-education/components/selectors/DepartmentTreeSelect";

const ACTION_LABELS: Record<string, string> = {
  NONE: "Hiçbir şey",
  ASSIGN_FIXED_DEPARTMENT: "Sabit departmana ata",
  ASK_DEPARTMENT_ON_REVIEW: "Departman seçimi iste",
  DELETE_USER: "Kullanıcı hesabını sil",
  DEACTIVATE_USER: "Kullanıcı hesabını pasife al",
};

const REVIEW_STATUS_LABELS: Record<string, string> = {
  PENDING: "Bekliyor",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
};

function SurveySubpageNav({ onSurveysClick }: { onSurveysClick: () => void }) {
  return (
    <nav className="flex-shrink-0 border-b border-gray-200 bg-white shadow-sm">
      <div className="px-4 py-2.5 sm:px-6 sm:py-3">
        <div className="flex min-w-0 items-center gap-4 sm:gap-6">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-semibold text-gray-900">Anketler</span>
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-4 overflow-x-auto text-sm text-gray-600 sm:gap-6 sm:overflow-visible">
            <button
              type="button"
              onClick={onSurveysClick}
              className="flex-shrink-0 border-b-2 border-gray-900 pb-2.5 font-semibold text-gray-900 transition-all duration-200 sm:pb-3">
              Tüm Anketler
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default function SurveyResults() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<any>(null);
  const [participants, setParticipants] = useState<any[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [responses, setResponses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<any>(null);
  const [participantModalLoading, setParticipantModalLoading] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [selectedReviewDepartmentId, setSelectedReviewDepartmentId] = useState("");
  const [reviewDepartments, setReviewDepartments] = useState<Array<{ id: string; name: string; color?: string; parentId?: string | null }>>([]);
  const [pendingReviewAction, setPendingReviewAction] = useState<{ decision: "APPROVE" | "REJECT"; participant: any } | null>(null);
  const [showReviewConfirmModal, setShowReviewConfirmModal] = useState(false);
  const [pendingSimpleReviewAction, setPendingSimpleReviewAction] = useState<{ decision: "APPROVE" | "REJECT"; participant: any; actionType: string } | null>(null);
  const [showDecisionChangeModal, setShowDecisionChangeModal] = useState(false);
  const [decisionChangeParticipant, setDecisionChangeParticipant] = useState<any>(null);
  const [reviewSubmittingKey, setReviewSubmittingKey] = useState("");
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [participantToReset, setParticipantToReset] = useState<any>(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [allDepartments, setAllDepartments] = useState<Array<{ id: string; parentId?: string | null; name: string }>>([]);
  const [showParticipantFilterModal, setShowParticipantFilterModal] = useState(false);
  const [participantCurrentPage, setParticipantCurrentPage] = useState(1);
  const [pendingDepartmentFilterIds, setPendingDepartmentFilterIds] = useState<string[]>([]);
  const [activeDepartmentFilterIds, setActiveDepartmentFilterIds] = useState<string[]>([]);
  const [participantSearchText, setParticipantSearchText] = useState("");

  const canViewResults = checkPermission("survey_update");
  const canReviewSurvey = canViewResults;

  const loadSurveyData = useCallback(async (showLoader: boolean = true) => {
    if (!id) return;

    if (!canViewResults) {
      setError("Bu sayfaya erişim yetkiniz bulunmamaktadır.");
      setLoading(false);
      return;
    }

    try {
      if (showLoader) {
        setLoading(true);
      }
      setError("");

      const resultsData = await ApiService.getSurveyResults(id);

      setSurvey(resultsData.survey);
      setParticipants(resultsData.participants || []);

      const questionStats = resultsData.questionStatistics || [];
      const formattedQuestions = questionStats
        .map((stat: any) => ({
          id: stat.questionId,
          title: stat.questionTitle,
          type: stat.questionType,
          answers: stat.answers || [],
          options: stat.options || [],
          totalAnswers: stat.totalAnswers,
          isMandatory: stat.isMandatory,
          order: stat.questionOrder ?? 0,
          sectionId: stat.sectionId || null,
          sectionTitle: stat.sectionTitle || null,
          sectionOrder: stat.sectionOrder ?? 0,
        }))
        .sort((a: any, b: any) => {
          const sectionOrderDiff = (a.sectionOrder ?? 0) - (b.sectionOrder ?? 0);
          if (sectionOrderDiff !== 0) return sectionOrderDiff;
          return (a.order ?? 0) - (b.order ?? 0);
        });
      setQuestions(formattedQuestions);

      const completedParticipants = resultsData.participants?.filter((p: any) => p.status === "COMPLETED") || [];
      const formattedResponses = completedParticipants.map((participant: any) => ({
        id: participant.userId,
        userId: participant.userId,
        status: participant.status,
        startDate: participant.startedAt,
        endDate: participant.completedAt,
        user: {
          firstName: participant.firstName,
          lastName: participant.lastName,
          email: participant.email,
          department: participant.department,
        },
      }));
      setResponses(formattedResponses);

    } catch (err: any) {
      console.error("Error fetching survey results:", err);
      setError(err.message || "Sonuçlar yüklenirken hata oluştu");
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, [id, canViewResults]);

  useEffect(() => {
    loadSurveyData(true);
  }, [loadSurveyData]);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await ApiService.getAllDepartments(null);
        const departmentData = response?.data || response || [];
        const normalized = (Array.isArray(departmentData) ? departmentData : [])
          .filter((department: any) => department?.id && !department?.deletedAt)
          .map((department: any) => ({
            id: String(department.id),
            parentId: department.parentId ? String(department.parentId) : null,
            name: String(department.name || department.title || ""),
          }));
        setAllDepartments(normalized);
      } catch (departmentError) {
        console.error("Departman ağacı yüklenemedi:", departmentError);
        setAllDepartments([]);
      }
    };

    loadDepartments();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDisplayEmail = (email?: string | null) => {
    if (!email) return "-";
    let normalized = String(email).trim();
    normalized = normalized.replace(/_user_[^@]+@deleted\.com$/i, "");
    normalized = normalized.replace(/__deleted_[^_]+_\d+$/i, "");
    return normalized;
  };

  const handleParticipantClick = async (participant: any) => {
    setSelectedParticipant(participant);
    setShowParticipantModal(true);
    setParticipantModalLoading(true);

    try {
      const participantAnswers: any[] = [];

      [...questions]
        .sort((a: any, b: any) => {
          const sectionOrderDiff = (a.sectionOrder ?? 0) - (b.sectionOrder ?? 0);
          if (sectionOrderDiff !== 0) return sectionOrderDiff;
          return (a.order ?? 0) - (b.order ?? 0);
        })
        .forEach((question) => {
        if (question.options && question.options.length > 0) {
          const userAnswers: string[] = [];
          question.options.forEach((option: any) => {
            const hasAnswered = option.responders?.some((r: any) => r.userId === participant.userId);
            if (hasAnswered) {
              userAnswers.push(option.value);
            }
          });

          if (userAnswers.length > 0) {
            participantAnswers.push({
              id: `answer_${question.id}`,
              surveyQuestionId: question.id,
              answer: userAnswers.length === 1 ? userAnswers[0] : userAnswers,
              question: {
                title: question.title,
                type: question.type,
                order: question.order,
                sectionTitle: question.sectionTitle,
                sectionOrder: question.sectionOrder,
              },
            });
          }
        } else {
          const userAnswer = question.answers?.find((ans: any) => ans.responder?.userId === participant.userId);
          if (userAnswer) {
            participantAnswers.push({
              id: `answer_${question.id}`,
              surveyQuestionId: question.id,
              answer: userAnswer.answer,
              answerMedia: userAnswer.answerMedia,
              question: {
                title: question.title,
                type: question.type,
                order: question.order,
                sectionTitle: question.sectionTitle,
                sectionOrder: question.sectionOrder,
              },
            });
          }
        }
      });

      setSelectedParticipant((prev: any) => ({
        ...prev,
        responses: {
          user: participant.user || participant,
          endDate: participant.endDate || participant.completedAt,
          answers: participantAnswers,
        },
      }));
    } catch (error) {
      console.error("Katılımcı cevapları yüklenirken hata:", error);
    } finally {
      setParticipantModalLoading(false);
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    switch (type) {
      case "TEXT":
        return "Metin";
      case "RADIO":
        return "Tek Seçim";
      case "CHECKBOX":
        return "Çoklu Seçim";
      case "FILE":
        return "Dosya";
      default:
        return type;
    }
  };

  const formatFileSize = (size?: number) => {
    if (!size && size !== 0) return "-";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getReviewStatusBadge = (status: string) => {
    if (status === "APPROVED") {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Onaylandı</span>;
    }
    if (status === "REJECTED") {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Reddedildi</span>;
    }
    return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Bekliyor</span>;
  };

  const departmentChildrenMap = useMemo(() => {
    const map = new Map<string, string[]>();
    allDepartments.forEach((department) => {
      if (!department.parentId) return;
      const currentChildren = map.get(department.parentId) || [];
      currentChildren.push(department.id);
      map.set(department.parentId, currentChildren);
    });
    return map;
  }, [allDepartments]);

  const activeDepartmentScope = useMemo(() => {
    if (activeDepartmentFilterIds.length === 0) {
      return null;
    }

    const scopedDepartmentIds = new Set<string>();
    const stack = [...activeDepartmentFilterIds];

    while (stack.length > 0) {
      const currentDepartmentId = stack.pop();
      if (!currentDepartmentId || scopedDepartmentIds.has(currentDepartmentId)) continue;
      scopedDepartmentIds.add(currentDepartmentId);
      const childDepartments = departmentChildrenMap.get(currentDepartmentId) || [];
      childDepartments.forEach((childId) => stack.push(childId));
    }

    return scopedDepartmentIds;
  }, [activeDepartmentFilterIds, departmentChildrenMap]);

  const filteredParticipants = useMemo(() => {
    if (!activeDepartmentScope) {
      return participants;
    }

    return participants.filter((participant) => {
      const participantDepartmentId = participant?.department?.id;
      if (!participantDepartmentId) return false;
      return activeDepartmentScope.has(String(participantDepartmentId));
    });
  }, [participants, activeDepartmentScope]);

  const searchedParticipants = useMemo(() => {
    const normalizedSearch = participantSearchText.trim().toLocaleLowerCase("tr-TR");
    if (!normalizedSearch) {
      return filteredParticipants;
    }

    return filteredParticipants.filter((participant) => {
      const fullName = `${participant?.firstName || ""} ${participant?.lastName || ""}`
        .trim()
        .toLocaleLowerCase("tr-TR");
      return fullName.includes(normalizedSearch);
    });
  }, [filteredParticipants, participantSearchText]);

  const filteredResponsesCount = useMemo(
    () => searchedParticipants.filter((participant) => participant.status === "COMPLETED").length,
    [searchedParticipants]
  );

  const participantFilterLabel = useMemo(() => {
    if (activeDepartmentFilterIds.length === 0) {
      return "Tüm departmanlar";
    }

    const selectedNames = activeDepartmentFilterIds
      .map((departmentId) => allDepartments.find((department) => department.id === departmentId)?.name)
      .filter(Boolean);

    if (selectedNames.length === 0) {
      return `${activeDepartmentFilterIds.length} departman seçili`;
    }

    return selectedNames.join(", ");
  }, [activeDepartmentFilterIds, allDepartments]);

  const participantListRefreshKey = useMemo(
    () =>
      JSON.stringify({
        filters: activeDepartmentFilterIds,
        search: participantSearchText,
        participants: searchedParticipants.map((participant) => ({
          userId: participant.userId,
          status: participant.status,
          reviewStatus: participant.reviewStatus,
          reviewedAt: participant.reviewedAt || null,
          completedAt: participant.completedAt || null,
          departmentId: participant?.department?.id || null,
          reviewActionLocked: participant.reviewActionLocked || false,
        })),
      }),
    [activeDepartmentFilterIds, searchedParticipants, participantSearchText]
  );

  const participantPaginationResetKey = useMemo(
    () =>
      JSON.stringify({
        filters: activeDepartmentFilterIds,
        search: participantSearchText,
      }),
    [activeDepartmentFilterIds, participantSearchText]
  );

  useEffect(() => {
    setParticipantCurrentPage(1);
  }, [participantPaginationResetKey]);

  const renderAnswer = (answer: any) => {
    const fileMeta = answer?.answerMedia || answer?.answer;
    if (fileMeta && typeof fileMeta === "object" && (fileMeta.url || fileMeta.fileName || fileMeta.name)) {
      const fileUrl = fileMeta.url;
      const fileName = fileMeta.fileName || fileMeta.name || "Dosya";
      const fileSize = formatFileSize(fileMeta.size);

      if (fileUrl) {
        return (
          <a
            href={fileUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline break-all">
            {fileName} ({fileSize})
          </a>
        );
      }
      return `${fileName} (${fileSize})`;
    }

    if (Array.isArray(answer.answer)) {
      return answer.answer.join(", ");
    }

    return String(answer.answer || "Cevapsız");
  };

  const getGroupedParticipantAnswers = (answers: any[] = []) => {
    const groupedMap = new Map<
      string,
      {
        sectionTitle: string;
        sectionOrder: number;
        answers: any[];
      }
    >();

    answers.forEach((answer) => {
      const sectionTitle = answer?.question?.sectionTitle || "Bolumsuz";
      const sectionOrder = answer?.question?.sectionOrder ?? Number.MAX_SAFE_INTEGER;
      const mapKey = `${sectionOrder}_${sectionTitle}`;

      if (!groupedMap.has(mapKey)) {
        groupedMap.set(mapKey, {
          sectionTitle,
          sectionOrder,
          answers: [],
        });
      }

      groupedMap.get(mapKey)?.answers.push(answer);
    });

    return Array.from(groupedMap.values())
      .sort((a, b) => a.sectionOrder - b.sectionOrder)
      .map((group) => ({
        ...group,
        answers: [...group.answers].sort((a, b) => (a?.question?.order ?? 0) - (b?.question?.order ?? 0)),
      }));
  };

  const getDecisionActionType = (decision: "APPROVE" | "REJECT") => {
    if (!survey?.isActionEnabled) return "NONE";
    return decision === "APPROVE" ? survey?.approveActionType || "NONE" : survey?.rejectActionType || "NONE";
  };

  const getLatestReviewHistoryItem = (participant: any) => {
    if (!Array.isArray(participant?.reviewHistory) || participant.reviewHistory.length === 0) {
      return null;
    }
    return participant.reviewHistory[participant.reviewHistory.length - 1];
  };

  const getParticipantActionSummary = (participant: any) => {
    const latestHistory = getLatestReviewHistoryItem(participant);
    if (latestHistory?.sideEffectSummary) {
      return String(latestHistory.sideEffectSummary).replace(" (deletedAt işaretlendi)", "");
    }
    return "Henüz işlem uygulanmadı";
  };

  const getHistoryDepartmentName = (historyItem: any) => {
    if (historyItem?.departmentName) return historyItem.departmentName;
    if (
      historyItem?.departmentId &&
      selectedParticipant?.reviewDepartmentId === historyItem.departmentId &&
      selectedParticipant?.reviewDepartmentName
    ) {
      return selectedParticipant.reviewDepartmentName;
    }
    if (selectedParticipant?.department?.name) {
      return selectedParticipant.department.name;
    }
    if (selectedParticipant?.responses?.user?.department?.name) {
      return selectedParticipant.responses.user.department.name;
    }
    return "-";
  };

  const hasReviewDecision = (participant: any) => {
    return participant?.reviewStatus === "APPROVED" || participant?.reviewStatus === "REJECTED";
  };

  const getDecisionChangeOptions = (participant: any): Array<"APPROVE" | "REJECT"> => {
    if (participant?.reviewStatus === "APPROVED") {
      return ["REJECT"];
    }
    if (participant?.reviewStatus === "REJECTED") {
      return ["APPROVE"];
    }
    return ["APPROVE", "REJECT"];
  };

  const canOpenDecisionChange = (participant: any) => {
    if (!participant) return false;
    if (!hasReviewDecision(participant)) return false;
    return !participant.reviewActionLocked;
  };

  const loadReviewDepartments = async () => {
    if (!id) return;
    const departments = await ApiService.getSurveyReviewDepartments(id);
    setReviewDepartments(Array.isArray(departments) ? departments : []);
  };

  const submitReview = async (participant: any, decision: "APPROVE" | "REJECT", departmentId?: string | null) => {
    if (!id) return;

    const submittingKey = `${participant.userId}_${decision}`;
    setReviewSubmittingKey(submittingKey);
    try {
      await ApiService.reviewSurveyResponse(id, participant.userId, {
        decision,
        departmentId: departmentId || undefined,
      });
      await loadSurveyData(false);
      if (selectedParticipant?.userId === participant.userId) {
        setShowParticipantModal(false);
        setSelectedParticipant(null);
      }
    } catch (err: any) {
      setError(err?.message || "Review işlemi sırasında hata oluştu");
    } finally {
      setReviewSubmittingKey("");
    }
  };

  const handleReviewClick = async (participant: any, decision: "APPROVE" | "REJECT") => {
    const actionType = getDecisionActionType(decision);
    if (actionType === "ASK_DEPARTMENT_ON_REVIEW") {
      try {
        await loadReviewDepartments();
        setSelectedReviewDepartmentId("");
        setPendingReviewAction({ participant, decision });
        setShowDepartmentModal(true);
      } catch (err: any) {
        setError(err?.message || "Departman listesi alınamadı");
      }
      return;
    }

    setPendingSimpleReviewAction({
      participant,
      decision,
      actionType,
    });
    setShowReviewConfirmModal(true);
  };

  const confirmDepartmentReview = async () => {
    if (!pendingReviewAction || !selectedReviewDepartmentId) return;
    await submitReview(
      pendingReviewAction.participant,
      pendingReviewAction.decision,
      selectedReviewDepartmentId
    );
    setShowDepartmentModal(false);
    setPendingReviewAction(null);
    setSelectedReviewDepartmentId("");
  };

  const handleResetClick = (participant: any) => {
    setParticipantToReset(participant);
    setShowResetConfirmModal(true);
  };

  const handleResetConfirm = async () => {
    if (!id || !participantToReset) return;

    setResetSubmitting(true);
    try {
      await ApiService.resetSurveyResponse(id, participantToReset.userId);
      await loadSurveyData(false);

      if (selectedParticipant?.userId === participantToReset.userId) {
        setShowParticipantModal(false);
        setSelectedParticipant(null);
      }

      setShowResetConfirmModal(false);
      setParticipantToReset(null);
    } catch (err: any) {
      setError(err?.message || "Katılımcı cevapları sıfırlanırken hata oluştu");
    } finally {
      setResetSubmitting(false);
    }
  };

  const renderParticipantItem = useCallback((participant: any) => {
    const reviewed = hasReviewDecision(participant);
    const reviewedCardClass = reviewed
      ? "border-emerald-200 bg-emerald-50/50 shadow-sm ring-1 ring-emerald-100"
      : "border-slate-200 bg-white";
    const clickClass =
      participant.status === "COMPLETED"
        ? "cursor-pointer hover:border-slate-300 hover:bg-slate-50/80"
        : "bg-slate-50/60";
    const canResetParticipant =
      participant.status === "COMPLETED" || participant.status === "IN_PROGRESS";

    return (
      <div
        key={participant.userId}
        className={`rounded-xl border p-3 transition-colors sm:p-4 ${reviewed ? "border-l-4 border-l-emerald-500" : ""} ${clickClass} ${reviewedCardClass}`}
        onClick={() => participant.status === "COMPLETED" && handleParticipantClick(participant)}>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between md:gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-100 ring-2 ring-white shadow-sm">
              <span className="text-sm font-semibold text-blue-700">
                {(participant.firstName || "").charAt(0)}
                {(participant.lastName || "").charAt(0)}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="break-words font-medium text-gray-900">
                {participant.firstName} {participant.lastName}
              </div>
              <div className="break-all text-sm text-gray-600">{getDisplayEmail(participant.email)}</div>
              {participant.department && (
                <div className="mt-1 flex items-start gap-1 text-xs text-slate-500">
                  <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="break-words">{participant.department.name}</span>
                </div>
              )}
              {participant.isDeleted && <div className="mt-1 text-xs text-red-600">Hesap silinmiş</div>}
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-col gap-3 border-t border-slate-100 pt-3 md:w-auto md:max-w-xl md:border-t-0 md:pt-0 lg:max-w-2xl">
            <div className="flex flex-wrap items-center gap-2 md:justify-end">
              {getReviewStatusBadge(participant.reviewStatus)}
              {participant.completedAt && (
                <div className="text-xs text-gray-500">{formatDate(participant.completedAt)}</div>
              )}
              {participant.status === "COMPLETED" && <Eye className="h-4 w-4 shrink-0 text-gray-400 md:hidden" aria-hidden />}
            </div>
            <div className="break-words text-xs leading-snug text-gray-500 md:text-right">
              <span className="font-medium text-gray-600">Yapılan işlem: </span>
              {getParticipantActionSummary(participant)}
            </div>
            <div className="flex flex-wrap items-center gap-2 md:justify-end">
              {canResetParticipant && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResetClick(participant);
                  }}
                  disabled={resetSubmitting}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-initial">
                  <RotateCcw className="h-3.5 w-3.5 shrink-0" />
                  Sıfırla
                </button>
              )}
              {canReviewSurvey && participant.status === "COMPLETED" && (
                <>
                  {hasReviewDecision(participant) ? (
                    canOpenDecisionChange(participant) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDecisionChangeParticipant(participant);
                          setShowDecisionChangeModal(true);
                        }}
                        className="inline-flex shrink-0 items-center justify-center rounded-lg border border-gray-300 p-2 text-gray-700 hover:bg-gray-50"
                        title="Kararı Değiştir">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )
                  ) : (
                    <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReviewClick(participant, "APPROVE");
                        }}
                        disabled={participant.reviewActionLocked || reviewSubmittingKey === `${participant.userId}_APPROVE`}
                        className="inline-flex min-h-[2.25rem] flex-1 items-center justify-center rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-initial sm:min-w-[5.5rem]">
                        Onayla
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReviewClick(participant, "REJECT");
                        }}
                        disabled={participant.reviewActionLocked || reviewSubmittingKey === `${participant.userId}_REJECT`}
                        className="inline-flex min-h-[2.25rem] flex-1 items-center justify-center rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-initial sm:min-w-[5.5rem]">
                        Reddet
                      </button>
                    </div>
                  )}
                </>
              )}
              {participant.status === "COMPLETED" && (
                <Eye className="hidden h-4 w-4 shrink-0 text-gray-400 md:inline-block" aria-hidden />
              )}
            </div>
          </div>
        </div>
        {participant.reviewActionLocked && participant.reviewLockReason && (
          <div className="mt-3 border-t border-slate-100 pt-3 text-xs text-red-600 md:mt-2 md:border-t-0 md:pt-0">
            {participant.reviewLockReason}
          </div>
        )}
      </div>
    );
  }, [
    canReviewSurvey,
    resetSubmitting,
    reviewSubmittingKey,
    handleParticipantClick,
    handleResetClick,
    handleReviewClick,
  ]);

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-60px)] w-full flex-col overflow-hidden bg-gray-100">
        <SurveySubpageNav onSurveysClick={() => navigate("/surveys")} />
        <div className="flex flex-1 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[calc(100vh-60px)] w-full flex-col overflow-hidden bg-gray-100">
        <SurveySubpageNav onSurveysClick={() => navigate("/surveys")} />
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            <h2 className="mb-2 text-xl font-semibold text-gray-900">Hata</h2>
            <p className="mb-6 text-gray-600">{error}</p>
            <button
              type="button"
              onClick={() => navigate("/surveys")}
              className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition hover:from-blue-700 hover:to-indigo-700">
              Anketler Listesine Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-[calc(100vh-60px)] w-full flex-col overflow-hidden bg-gray-100">
        <SurveySubpageNav onSurveysClick={() => navigate("/surveys")} />

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 sm:p-5 md:p-6">
          <div className="mx-auto w-full max-w-6xl space-y-4 sm:space-y-6">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 via-white to-blue-50/40 px-4 py-6 sm:px-8 sm:py-9">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wider text-blue-600/90">Anket sonuçları</p>
                    <h1 className="mt-2 text-xl font-semibold leading-tight text-gray-900 sm:text-2xl md:text-3xl">
                      {survey?.title}
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm text-gray-600">
                      Katılımcı durumu, tamamlanma ve soru özetleri bu sayfadan takip edilir.
                    </p>
                    <div className="mt-4 h-1 w-20 rounded-full bg-blue-200 sm:mt-5" />
                  </div>
                  <div className="grid w-full grid-cols-3 gap-2 sm:max-w-md sm:grid-cols-3 sm:justify-self-end lg:max-w-none lg:flex lg:w-auto lg:flex-wrap lg:justify-end lg:gap-3">
                    <div className="min-w-0 flex-1 rounded-xl border border-slate-200/90 bg-white/90 px-3 py-3 text-center shadow-sm backdrop-blur-sm sm:min-w-[5.5rem] sm:flex-none sm:px-5 sm:py-4 lg:min-w-[100px]">
                      <div className="text-lg font-semibold tabular-nums text-gray-900 sm:text-2xl">{participants.length}</div>
                      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-[11px]">
                        Katılımcı
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 rounded-xl border border-slate-200/90 bg-white/90 px-3 py-3 text-center shadow-sm backdrop-blur-sm sm:min-w-[5.5rem] sm:flex-none sm:px-5 sm:py-4 lg:min-w-[100px]">
                      <div className="text-lg font-semibold tabular-nums text-gray-900 sm:text-2xl">{responses.length}</div>
                      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-[11px]">
                        Tamamlanan
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 rounded-xl border border-slate-200/90 bg-white/90 px-3 py-3 text-center shadow-sm backdrop-blur-sm sm:min-w-[5.5rem] sm:flex-none sm:px-5 sm:py-4 lg:min-w-[100px]">
                      <div className="text-lg font-semibold tabular-nums text-gray-900 sm:text-2xl">{questions.length}</div>
                      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-[11px]">
                        Soru
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 md:p-6">
              <DynamicList
                header
                title="Katılımcı Durumu"
                path="survey-results-participants"
                fetcher={async () => ({ data: searchedParticipants })}
                refreshKey={participantListRefreshKey}
                isPagination
                clientPageSize={100}
                externalCurrentPage={participantCurrentPage}
                onPageChange={setParticipantCurrentPage}
                emptyMessage="Filtreye uygun katılımcı bulunmuyor."
                actions={
                  <div className="flex w-full flex-col gap-2 text-sm text-gray-600 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                    <div className="flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 sm:w-auto sm:shrink-0">
                      <Users className="h-4 w-4 shrink-0 text-slate-500" />
                      <span className="min-w-0 font-medium">
                        {filteredResponsesCount}/{searchedParticipants.length} kişi cevapladı
                      </span>
                    </div>
                    <div className="flex w-full min-w-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 sm:max-w-xs sm:flex-1 md:max-w-sm lg:max-w-md">
                      <Search className="h-4 w-4 shrink-0 text-slate-400" />
                      <input
                        type="text"
                        value={participantSearchText}
                        onChange={(event) => setParticipantSearchText(event.target.value)}
                        placeholder="Ad soyad ara"
                        className="min-w-0 flex-1 bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
                      />
                    </div>
                    {activeDepartmentFilterIds.length > 0 && (
                      <>
                        <span className="inline-flex max-w-full items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700">
                          <span className="truncate">{participantFilterLabel}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveDepartmentFilterIds([]);
                            setPendingDepartmentFilterIds([]);
                          }}
                          className="self-start px-1 py-2 text-xs text-red-600 hover:text-red-700 sm:self-auto">
                          Temizle
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setPendingDepartmentFilterIds(activeDepartmentFilterIds);
                        setShowParticipantFilterModal(true);
                      }}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-gray-700 shadow-sm transition hover:bg-slate-50 sm:w-auto sm:shrink-0">
                      <Filter className="h-4 w-4 shrink-0" />
                      Filtrele
                    </button>
                  </div>
                }
                item={renderParticipantItem}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showParticipantModal}
        onClose={() => {
          setShowParticipantModal(false);
          setSelectedParticipant(null);
        }}
        title={`${selectedParticipant?.user?.firstName || selectedParticipant?.responses?.user?.firstName} ${
          selectedParticipant?.user?.lastName || selectedParticipant?.responses?.user?.lastName
        } - Anket Cevapları`}
        size="lg">
        {participantModalLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : selectedParticipant?.responses ? (
          <div className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-blue-50/30 p-3 sm:p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-sm ring-2 ring-blue-100">
                  {selectedParticipant.responses.user?.media?.url ? (
                    <img
                      src={selectedParticipant.responses.user.media.url}
                      alt={`${selectedParticipant.responses.user.firstName} ${selectedParticipant.responses.user.lastName}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-lg font-semibold text-blue-700">
                      {(selectedParticipant.responses.user?.firstName || "").charAt(0)}
                      {(selectedParticipant.responses.user?.lastName || "").charAt(0)}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="break-words font-semibold text-gray-900">
                    {selectedParticipant.responses.user?.firstName} {selectedParticipant.responses.user?.lastName}
                  </div>
                  <div className="break-all text-sm text-gray-600">{getDisplayEmail(selectedParticipant.responses.user?.email)}</div>
                  {selectedParticipant.responses.endDate && (
                    <div className="text-xs text-gray-500">Tamamlanma: {formatDate(selectedParticipant.responses.endDate)}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Verilen Cevaplar:</h3>
              {getGroupedParticipantAnswers(selectedParticipant.responses.answers).map((sectionGroup, sectionIndex) => (
                <div
                  key={`${sectionGroup.sectionTitle}_${sectionIndex}`}
                  className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-100 bg-slate-50/90 px-4 py-3">
                    <div className="font-semibold text-gray-900">
                      {sectionIndex + 1}. {sectionGroup.sectionTitle}
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    {sectionGroup.answers.map((answer: any, index: number) => (
                      <div key={answer.id} className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                        <div className="flex items-start gap-3">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 mb-2">{answer.question?.title}</div>
                            <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-gray-700 break-words overflow-hidden">
                              {renderAnswer(answer)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-3 border-t border-gray-200 pt-4">
              <h3 className="font-semibold text-gray-900">Değerlendirme Bilgileri</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <div className="text-gray-500">Durum</div>
                  <div className="font-medium text-gray-900">
                    {REVIEW_STATUS_LABELS[selectedParticipant?.reviewStatus || "PENDING"] || "Bekliyor"}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <div className="text-gray-500">Uygulanan Aksiyon</div>
                  <div className="font-medium text-gray-900">
                    {ACTION_LABELS[selectedParticipant?.reviewActionType || "NONE"] || "Hiçbir şey"}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <div className="text-gray-500">Gittiği Departman</div>
                  <div className="font-medium text-gray-900">
                    {selectedParticipant?.reviewDepartmentName || "-"}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <div className="text-gray-500">İnceleme Zamanı</div>
                  <div className="font-medium text-gray-900">
                    {selectedParticipant?.reviewedAt ? formatDate(selectedParticipant.reviewedAt) : "-"}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-800">Değerlendirme Geçmişi</div>
                {Array.isArray(selectedParticipant?.reviewHistory) && selectedParticipant.reviewHistory.length > 0 ? (
                  <div className="space-y-2">
                    {selectedParticipant.reviewHistory.map((historyItem: any, historyIndex: number) => (
                      <div key={`${historyItem.at}_${historyIndex}`} className="text-xs bg-gray-50 border border-gray-200 rounded p-2 text-gray-700">
                        <div>Tarih: {historyItem.at ? formatDate(historyItem.at) : "-"}</div>
                        <div>Durum: {REVIEW_STATUS_LABELS[historyItem.fromStatus] || historyItem.fromStatus} → {REVIEW_STATUS_LABELS[historyItem.toStatus] || historyItem.toStatus}</div>
                        <div>Aksiyon: {ACTION_LABELS[historyItem.actionType] || historyItem.actionType || "-"}</div>
                        <div>Departman: {getHistoryDepartmentName(historyItem)}</div>
                        <div>Özet: {historyItem.sideEffectSummary ? String(historyItem.sideEffectSummary).replace(" (deletedAt işaretlendi)", "") : "-"}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">Henüz değerlendirme geçmişi bulunmuyor.</div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>Katılımcı cevapları yüklenemedi.</p>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showParticipantFilterModal}
        onClose={() => {
          setShowParticipantFilterModal(false);
          setPendingDepartmentFilterIds(activeDepartmentFilterIds);
        }}
        title="Katılımcı Filtrele"
        overflowVisible
        size="md">
        <div className="space-y-4 overflow-visible">
          <div className="text-sm text-gray-600">
            Departman seçin. Üst departman seçildiğinde alt departmanlardaki katılımcılar da filtreye dahil edilir.
          </div>
          <div className="overflow-visible">
            <label className="block text-sm font-medium text-gray-700 mb-2">Departman</label>
            <DepartmentTreeSelect
              value={pendingDepartmentFilterIds}
              onChange={(departmentIds) => setPendingDepartmentFilterIds(departmentIds)}
              multiple={false}
            />
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <button
              type="button"
              onClick={() => {
                setPendingDepartmentFilterIds([]);
              }}
              className="w-full px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 sm:w-auto">
              Temizle
            </button>
            <button
              type="button"
              onClick={() => {
                setShowParticipantFilterModal(false);
                setPendingDepartmentFilterIds(activeDepartmentFilterIds);
              }}
              className="w-full px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 sm:w-auto">
              Vazgeç
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveDepartmentFilterIds(pendingDepartmentFilterIds);
                setShowParticipantFilterModal(false);
              }}
              className="w-full px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 sm:w-auto">
              Uygula
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showResetConfirmModal}
        onClose={() => {
          if (resetSubmitting) return;
          setShowResetConfirmModal(false);
          setParticipantToReset(null);
        }}
        title="Cevapları Sıfırla"
        size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            <strong>{participantToReset?.firstName} {participantToReset?.lastName}</strong> için kaydedilmiş tüm anket cevapları silinecek.
            Kullanıcı sonuç listesinden cevaplamamış duruma dönecek ve ankete yeniden katılabilecek.
          </p>
          <div className="text-sm bg-amber-50 border border-amber-200 rounded p-3 text-amber-800">
            Bu işlem kullanıcıya ait dosyalı cevapları da kaldırır.
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowResetConfirmModal(false);
                setParticipantToReset(null);
              }}
              disabled={resetSubmitting}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleResetConfirm}
              disabled={resetSubmitting}
              className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
              {resetSubmitting ? "Sıfırlanıyor..." : "Evet, sıfırla"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDecisionChangeModal}
        onClose={() => {
          setShowDecisionChangeModal(false);
          setDecisionChangeParticipant(null);
        }}
        title="Karar Değiştir"
        size="md">
        <div className="space-y-4">
          <div className="text-sm bg-gray-50 border border-gray-200 rounded p-3 text-gray-700">
            <div>
              Verilen karar:{" "}
              <strong>{REVIEW_STATUS_LABELS[decisionChangeParticipant?.reviewStatus || "PENDING"] || "Bekliyor"}</strong>
            </div>
            <div className="mt-1">
              Yapılan işlem: <strong>{getParticipantActionSummary(decisionChangeParticipant)}</strong>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {getDecisionChangeOptions(decisionChangeParticipant).map((decisionOption) => (
              <button
                key={decisionOption}
                type="button"
                onClick={() => {
                  if (!decisionChangeParticipant) return;
                  setShowDecisionChangeModal(false);
                  handleReviewClick(decisionChangeParticipant, decisionOption);
                }}
                className={`px-3 py-2 rounded-lg text-sm font-medium text-white ${decisionOption === "APPROVE" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}>
                {decisionOption === "APPROVE" ? "Onayla" : "Reddet"}
              </button>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDepartmentModal}
        onClose={() => {
          setShowDepartmentModal(false);
          setPendingReviewAction(null);
          setSelectedReviewDepartmentId("");
        }}
        title="Departman Seçimi"
        overflowVisible
        size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {pendingReviewAction?.decision === "APPROVE" ? "Onay" : "Ret"} kararı için hedef departmanı seçin.
          </p>
          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
            İşlem: {ACTION_LABELS[getDecisionActionType(pendingReviewAction?.decision || "APPROVE")] || "Departman seçimi iste"}
          </div>
          <DepartmentSelector
            value={selectedReviewDepartmentId || ""}
            onChange={(departmentId) => setSelectedReviewDepartmentId(departmentId || "")}
            placeholder="Departman seçiniz"
            departmentOptions={reviewDepartments}
            openDirection="up"
            className="w-full rounded-xl"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowDepartmentModal(false);
                setPendingReviewAction(null);
                setSelectedReviewDepartmentId("");
              }}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              Vazgeç
            </button>
            <button
              type="button"
              onClick={confirmDepartmentReview}
              disabled={!selectedReviewDepartmentId}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              Evet, uygula
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showReviewConfirmModal}
        onClose={() => {
          setShowReviewConfirmModal(false);
          setPendingSimpleReviewAction(null);
        }}
        title="İşlem Onayı"
        size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Bu katılımcı için <strong>{pendingSimpleReviewAction?.decision === "APPROVE" ? "Onayla" : "Reddet"}</strong> işlemini uygulamak istediğinize emin misiniz?
          </p>
          <div className="text-sm bg-gray-50 border border-gray-200 rounded p-3 text-gray-700">
            Yapılacak işlem: {ACTION_LABELS[pendingSimpleReviewAction?.actionType || "NONE"] || "Hiçbir şey"}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowReviewConfirmModal(false);
                setPendingSimpleReviewAction(null);
              }}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              Vazgeç
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!pendingSimpleReviewAction) return;
                await submitReview(
                  pendingSimpleReviewAction.participant,
                  pendingSimpleReviewAction.decision
                );
                setShowReviewConfirmModal(false);
                setPendingSimpleReviewAction(null);
              }}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700">
              Evet, uygula
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
